// =============================================================================
// HUB COMMUNICATION - Messagerie Instantanée
// =============================================================================
// Remplace WhatsApp pour la communication interne
// =============================================================================

import { useState, useEffect, useRef } from "react";
import {
  MessageSquare,
  Search,
  Plus,
  Send,
  Paperclip,
  Image,
  File,
  X,
  Users,
  MoreVertical,
  ChevronLeft,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Smile,
  Camera,
  Wifi,
  WifiOff
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MessagingPage({ tenantId, currentUser, onNavigate }) {
  // États principaux
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // États UI
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newConversationTitle, setNewConversationTitle] = useState("");
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showConversationList, setShowConversationList] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // WebSocket connection
  const connectWebSocket = (conversationId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const userId = currentUser?.user_id || localStorage.getItem("user_id") || "guest";
    const userName = currentUser?.nom || localStorage.getItem("user_name") || "Utilisateur";
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = API.replace(/^https?:/, wsProtocol);
    
    try {
      const ws = new WebSocket(
        `${wsUrl}/api/messaging/ws/${conversationId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`
      );
      
      ws.onopen = () => {
        console.log("WebSocket connecté");
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "new_message") {
            // Nouveau message reçu
            setMessages(prev => [...prev, data.message]);
            scrollToBottom();
          } else if (data.type === "user_typing") {
            // Indicateur de frappe (optionnel)
            console.log(`${data.user_name} est en train d'écrire...`);
          } else if (data.type === "messages_read") {
            // Marquer les messages comme lus
            setMessages(prev => prev.map(msg => 
              data.message_ids.includes(msg.id) 
                ? { ...msg, read_by: [...(msg.read_by || []), data.user_id] }
                : msg
            ));
          }
        } catch (e) {
          console.error("Erreur parsing WS message:", e);
        }
      };
      
      ws.onclose = () => {
        console.log("WebSocket déconnecté");
        setWsConnected(false);
      };
      
      ws.onerror = (error) => {
        console.error("WebSocket erreur:", error);
        setWsConnected(false);
      };
      
      wsRef.current = ws;
    } catch (e) {
      console.error("Erreur création WebSocket:", e);
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Charger les conversations au démarrage
  useEffect(() => {
    loadConversations();
    loadAvailableUsers();
    
    // Polling pour les conversations (liste) toutes les 10 secondes
    // Les messages sont gérés par WebSocket
    pollIntervalRef.current = setInterval(() => {
      loadConversations(true);
    }, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [tenantId]);

  // Charger les messages et connecter WebSocket quand on change de conversation
  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
      connectWebSocket(activeConversation.id);
      if (isMobileView) {
        setShowConversationList(false);
      }
    }
  }, [activeConversation?.id]);

  // Scroll vers le bas quand nouveaux messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getHeaders = () => {
    const token = localStorage.getItem("session_token");
    const userId = currentUser?.user_id || localStorage.getItem("user_id") || "";
    const userName = currentUser?.nom || localStorage.getItem("user_name") || "Utilisateur";
    const userRole = currentUser?.role || localStorage.getItem("user_role") || "commercial";
    
    return {
      "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
      "X-User-ID": userId,
      "X-User-Name": userName,
      "X-User-Role": userRole,
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` })
    };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ===================== API CALLS =====================

  const loadConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/messaging/conversations?limit=50`, {
        headers: getHeaders(),
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error("Erreur chargement conversations:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadMessages = async (conversationId, silent = false) => {
    try {
      const res = await fetch(
        `${API}/api/messaging/conversations/${conversationId}/messages?limit=100`,
        {
          headers: getHeaders(),
          credentials: "include"
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        // Les messages sont triés du plus récent au plus ancien, inverser
        setMessages(data.messages?.reverse() || []);
      }
    } catch (error) {
      console.error("Erreur chargement messages:", error);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const res = await fetch(`${API}/api/hub/users?limit=50`, {
        headers: getHeaders(),
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableUsers(data.users || data || []);
      }
    } catch (error) {
      console.error("Erreur chargement utilisateurs:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageInput.trim() || !activeConversation || sending) return;
    
    setSending(true);
    const content = messageInput;
    setMessageInput("");
    
    try {
      const formData = new FormData();
      formData.append("content", content);
      formData.append("type", "text");
      
      const res = await fetch(
        `${API}/api/messaging/conversations/${activeConversation.id}/messages`,
        {
          method: "POST",
          headers: {
            "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
            "X-User-ID": currentUser?.user_id || localStorage.getItem("user_id") || "",
            "X-User-Name": currentUser?.nom || localStorage.getItem("user_name") || "Utilisateur",
            "X-User-Role": currentUser?.role || localStorage.getItem("user_role") || "commercial",
            ...(localStorage.getItem("session_token") && {
              Authorization: `Bearer ${localStorage.getItem("session_token")}`
            })
          },
          credentials: "include",
          body: formData
        }
      );
      
      if (res.ok) {
        // Recharger les messages
        await loadMessages(activeConversation.id);
        await loadConversations(true);
      } else {
        setMessageInput(content); // Restaurer le message si erreur
      }
    } catch (error) {
      console.error("Erreur envoi message:", error);
      setMessageInput(content);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConversation) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("message_type", file.type.startsWith("image/") ? "image" : "file");
      
      const res = await fetch(
        `${API}/api/messaging/conversations/${activeConversation.id}/upload`,
        {
          method: "POST",
          headers: {
            "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
            "X-User-ID": currentUser?.user_id || localStorage.getItem("user_id") || "",
            "X-User-Name": currentUser?.nom || localStorage.getItem("user_name") || "Utilisateur",
            "X-User-Role": currentUser?.role || localStorage.getItem("user_role") || "commercial",
            ...(localStorage.getItem("session_token") && {
              Authorization: `Bearer ${localStorage.getItem("session_token")}`
            })
          },
          credentials: "include",
          body: formData
        }
      );
      
      if (res.ok) {
        await loadMessages(activeConversation.id);
        await loadConversations(true);
      }
    } catch (error) {
      console.error("Erreur upload fichier:", error);
    } finally {
      setSending(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      const res = await fetch(`${API}/api/messaging/conversations`, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({
          title: newConversationTitle || null,
          participant_ids: selectedUsers.map(u => u.user_id)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowNewConversation(false);
        setSelectedUsers([]);
        setNewConversationTitle("");
        await loadConversations();
        
        // Ouvrir la nouvelle conversation
        if (data.conversation_id) {
          const newConv = { id: data.conversation_id, title: newConversationTitle };
          setActiveConversation(newConv);
        }
      }
    } catch (error) {
      console.error("Erreur création conversation:", error);
    }
  };

  // ===================== HELPERS =====================

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Hier " + date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + 
           " " + date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const getFilteredConversations = () => {
    if (!searchQuery) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(c => 
      c.title?.toLowerCase().includes(query) ||
      c.client_name?.toLowerCase().includes(query) ||
      c.dossier_reference?.toLowerCase().includes(query)
    );
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  };

  // ===================== RENDER =====================

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-[600px]" data-testid="messaging-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] min-h-[600px] flex bg-slate-900 rounded-xl overflow-hidden border border-slate-700/50" data-testid="messaging-page">
      
      {/* ==================== LISTE CONVERSATIONS ==================== */}
      <div 
        className={`${
          isMobileView 
            ? (showConversationList ? "w-full" : "hidden") 
            : "w-80 border-r border-slate-700/50"
        } flex flex-col bg-slate-800/50`}
        data-testid="conversation-list"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Messages
            </h2>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => setShowNewConversation(true)}
              data-testid="new-conversation-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-700/50 border-slate-600 text-sm"
              data-testid="conversation-search"
            />
          </div>
        </div>
        
        {/* Liste */}
        <ScrollArea className="flex-1">
          {getFilteredConversations().length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
              <p className="text-slate-400 text-sm">Aucune conversation</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-slate-600"
                onClick={() => setShowNewConversation(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nouvelle discussion
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30">
              {getFilteredConversations().map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setActiveConversation(conv)}
                  className={`w-full p-3 text-left hover:bg-slate-700/50 transition-colors ${
                    activeConversation?.id === conv.id ? "bg-purple-500/20 border-l-2 border-purple-500" : ""
                  }`}
                  data-testid={`conversation-item-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-medium text-sm shrink-0">
                      {getInitials(conv.client_name || conv.title)}
                    </div>
                    
                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium truncate text-sm">
                          {conv.title || conv.client_name || "Discussion"}
                        </span>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-purple-500 text-white text-xs px-1.5 ml-2">
                            {conv.unread_count}
                          </Badge>
                        )}
                      </div>
                      
                      {conv.dossier_reference && (
                        <p className="text-purple-400 text-xs truncate">
                          {conv.dossier_reference}
                        </p>
                      )}
                      
                      {conv.last_message && (
                        <p className="text-slate-400 text-xs truncate mt-1">
                          {conv.last_message.sender_name}: {conv.last_message.content}
                        </p>
                      )}
                      
                      <p className="text-zinc-400 text-xs mt-1">
                        {formatTime(conv.last_activity_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
      
      {/* ==================== ZONE DE CHAT ==================== */}
      <div 
        className={`${
          isMobileView 
            ? (showConversationList ? "hidden" : "w-full") 
            : "flex-1"
        } flex flex-col`}
        data-testid="chat-area"
      >
        {activeConversation ? (
          <>
            {/* Header de la conversation */}
            <div className="p-4 border-b border-slate-700/50 bg-slate-800/30 flex items-center gap-3">
              {isMobileView && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConversationList(true)}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              )}
              
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-medium">
                {getInitials(activeConversation.client_name || activeConversation.title)}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium truncate flex items-center gap-2">
                  {activeConversation.title || activeConversation.client_name || "Discussion"}
                  {wsConnected ? (
                    <Wifi className="w-3 h-3 text-green-400" title="Temps réel actif" />
                  ) : (
                    <WifiOff className="w-3 h-3 text-orange-400" title="Mode hors-ligne" />
                  )}
                </h3>
                {activeConversation.participants && (
                  <p className="text-slate-400 text-xs flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {activeConversation.participants.length} participants
                  </p>
                )}
              </div>
              
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
                    <p className="text-slate-400">Aucun message</p>
                    <p className="text-zinc-400 text-sm">Commencez la conversation !</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.is_mine ? "justify-end" : "justify-start"}`}
                      data-testid={`message-${msg.id}`}
                    >
                      {msg.type === "system" ? (
                        <div className="px-4 py-2 bg-slate-700/30 rounded-full text-slate-400 text-xs text-center">
                          {msg.content}
                        </div>
                      ) : (
                        <div className={`max-w-[75%] ${msg.is_mine ? "items-end" : "items-start"}`}>
                          {!msg.is_mine && (
                            <p className="text-xs text-zinc-400 mb-1 ml-1">
                              {msg.sender_name}
                            </p>
                          )}
                          
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              msg.is_mine
                                ? "bg-purple-600 text-white rounded-br-sm"
                                : "bg-slate-700 text-white rounded-bl-sm"
                            }`}
                          >
                            {/* Pièces jointes */}
                            {msg.attachments?.length > 0 && (
                              <div className="mb-2">
                                {msg.attachments.map((att, attIdx) => (
                                  <div key={attIdx}>
                                    {att.mime_type?.startsWith("image/") ? (
                                      <img
                                        src={`${API}${att.url}`}
                                        alt={att.filename}
                                        className="max-w-full rounded-lg mb-2"
                                        style={{ maxHeight: 200 }}
                                      />
                                    ) : (
                                      <a
                                        href={`${API}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 p-2 bg-slate-600/50 rounded-lg hover:bg-slate-600"
                                      >
                                        <File className="w-5 h-5" />
                                        <span className="text-sm truncate">{att.filename}</span>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Contenu texte */}
                            {msg.content && !msg.content.startsWith("📎") && (
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {msg.content}
                              </p>
                            )}
                          </div>
                          
                          <div className={`flex items-center gap-1 mt-1 text-xs text-zinc-400 ${msg.is_mine ? "justify-end" : ""}`}>
                            <span>{formatTime(msg.created_at)}</span>
                            {msg.is_mine && (
                              <span>
                                {msg.read_by?.length > 1 ? (
                                  <CheckCheck className="w-3 h-3 text-blue-400" />
                                ) : (
                                  <Check className="w-3 h-3" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            {/* Zone de saisie */}
            <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
              <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                {/* Boutons d'attachement */}
                <div className="flex gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-white hover:bg-slate-700"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    data-testid="attach-file-btn"
                  >
                    <Paperclip className="w-5 h-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-white hover:bg-slate-700 md:flex hidden"
                    onClick={() => {
                      fileInputRef.current.accept = "image/*";
                      fileInputRef.current?.click();
                    }}
                    disabled={sending}
                  >
                    <Camera className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Input */}
                <div className="flex-1">
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Tapez votre message..."
                    className="bg-slate-700/50 border-slate-600"
                    disabled={sending}
                    data-testid="message-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                </div>
                
                {/* Bouton envoyer */}
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={!messageInput.trim() || sending}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Clock className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </form>
            </div>
          </>
        ) : (
          /* Placeholder quand pas de conversation sélectionnée */
          <div className="flex-1 flex items-center justify-center bg-slate-800/20">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
              <h3 className="text-white font-medium mb-2">Messagerie</h3>
              <p className="text-slate-400 text-sm max-w-xs mx-auto">
                Sélectionnez une conversation ou créez-en une nouvelle pour commencer
              </p>
              <Button
                className="mt-4 bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowNewConversation(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle conversation
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* ==================== MODAL NOUVELLE CONVERSATION ==================== */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-conversation-modal">
          <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Nouvelle conversation</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedUsers([]);
                  setNewConversationTitle("");
                }}
              >
                <X className="w-5 h-5 text-slate-400" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Titre optionnel */}
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Titre (optionnel)</label>
                <Input
                  value={newConversationTitle}
                  onChange={(e) => setNewConversationTitle(e.target.value)}
                  placeholder="Ex: Discussion projet X"
                  className="bg-slate-700/50 border-slate-600"
                  data-testid="new-conv-title"
                />
              </div>
              
              {/* Sélection participants */}
              <div>
                <label className="text-slate-400 text-sm mb-2 block">Participants</label>
                
                {/* Participants sélectionnés */}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedUsers.map((user) => (
                      <Badge
                        key={user.user_id}
                        className="bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] pr-1"
                      >
                        {user.name || user.email}
                        <button
                          onClick={() => setSelectedUsers(selectedUsers.filter(u => u.user_id !== user.user_id))}
                          className="ml-1 hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Liste des utilisateurs */}
                <ScrollArea className="h-48 border border-slate-700 rounded-lg">
                  <div className="p-2 space-y-1">
                    {availableUsers
                      .filter(u => !selectedUsers.find(s => s.user_id === u.user_id))
                      .map((user) => (
                        <button
                          key={user.user_id}
                          onClick={() => setSelectedUsers([...selectedUsers, user])}
                          className="w-full p-2 rounded hover:bg-slate-700/50 flex items-center gap-3 text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs font-medium">
                            {getInitials(user.name || user.email)}
                          </div>
                          <div>
                            <p className="text-white text-sm">{user.name || user.email}</p>
                            <p className="text-zinc-400 text-xs">{user.role}</p>
                          </div>
                        </button>
                      ))}
                    
                    {availableUsers.length === 0 && (
                      <p className="text-zinc-400 text-sm text-center py-4">
                        Aucun utilisateur disponible
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-700 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-slate-600"
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedUsers([]);
                  setNewConversationTitle("");
                }}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={selectedUsers.length === 0}
                onClick={handleCreateConversation}
                data-testid="create-conversation-btn"
              >
                Créer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
